// Mock Review: drill into a completed sitting question by question. Four
// tabs mirror the sitting order (L / R / W / S). L/R rows show your answer,
// the correct answer, whether you got it right, why the wrong choices attract
// (trap label + explanation). W/S rows show band + revisions + your text or
// transcript. Reachable from Results > Mocks and from teacher's student view.
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, ChevronDown, ChevronUp, Headphones, Mic, PenLine } from 'lucide-react';

import { api } from '@/lib/api';
import { dedupeGateFlags, gateFlagText, mockLabel, prettyTask, statusMeta } from '@/lib/labels';
import AppShell from '@/components/AppShell';
import ContentAudioPlayer from '@/components/ContentAudioPlayer';
import ContentImage from '@/components/ContentImage';
import DiagramCard from '@/components/DiagramCard';
import QuestionReviewRow from '@/components/QuestionReviewRow';
import SignedAudioPlayer from '@/components/SignedAudioPlayer';
import { cn } from '@/lib/utils';

const SECTION_ORDER = {
  celpip: ['listening', 'reading', 'writing', 'speaking'],
  pte: ['speaking', 'writing', 'reading', 'listening'],
};

const SECTION_META = {
  listening: { icon: Headphones, label: 'Listening' },
  reading: { icon: BookOpen, label: 'Reading' },
  writing: { icon: PenLine, label: 'Writing' },
  speaking: { icon: Mic, label: 'Speaking' },
};

const when = (iso) => (iso ? `${iso.slice(0, 10)} · ${iso.slice(11, 16)}` : '');
const pctCls = (p) => (p >= 75 ? 'text-emerald-600' : p >= 50 ? 'text-indigo-600' : 'text-red-600');

// One shape for options across mcq_single / mcq_multi / fib_dropdown so the
// renderer stays boring. Everything else (fib_typed, reorder, hiw, wfd) shows
// a simple your-answer vs correct-answer block. The row itself now lives in
// the shared QuestionReviewRow so Practice and Mock reviews stay in lockstep.

function ResponseRow({ resp, exam, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const st = statusMeta(resp.status);
  const zeroed = resp.status === 'gate_zeroed';
  const fb = resp.feedback || {};
  const band = !zeroed && (fb.band_estimate || fb.clb_band_estimate);
  // Speaking recordings ride through <SignedAudioPlayer>, which handles the
  // signed-URL fetch + a refetch-on-error retry if the URL's TTL expired
  // while the student read the feedback.
  return (
    <div className="border-t border-slate-100 first:border-t-0" data-testid={`review-response-${resp.submission_id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 px-5 py-3.5 text-left transition-colors hover:bg-slate-50"
      >
        <span className="min-w-0">
          <span className="block truncate font-display text-[15px] font-semibold text-slate-900">
            {resp.prompt?.title || prettyTask(resp.task_type)}
          </span>
          <span className="text-xs text-slate-400">{when(resp.created_at)}</span>
        </span>
        <span className="flex shrink-0 items-center gap-3">
          {band && <span className="h-display text-lg text-slate-900">{band}</span>}
          <span className={cn('rounded px-2 py-0.5 text-xs font-semibold', st.cls)}>{st.text}</span>
          {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
        </span>
      </button>
      {open && (
        <div className="space-y-4 bg-slate-50/60 px-5 py-5">
          {resp.prompt?.response_spec?.prompt_text && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Prompt</p>
              <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-slate-700">
                {resp.prompt.response_spec.prompt_text}
              </p>
            </div>
          )}
          {zeroed ? (
            <div data-testid={`review-response-${resp.submission_id}-gate-zeroed`}>
              <p className="text-sm font-semibold text-rose-700">Would score 0 on the real exam</p>
              <ul className="mt-1 space-y-1">
                {dedupeGateFlags(resp.gate_flags || ['empty']).map((flag, i) => (
                  <li key={i} className="text-sm leading-6 text-slate-700">{gateFlagText(flag)}</li>
                ))}
              </ul>
            </div>
          ) : (
            <>
              {/* PTE Answer Short Question renders as a right/wrong verdict,
                  not a band. Mirrors SubmissionReviewPage's ASQ block. */}
              {fb.kind === 'asq' && (
                <div
                  className={cn(
                    'border-l-2 px-4 py-3',
                    fb.correct ? 'border-emerald-500 bg-emerald-50/50' : 'border-rose-500 bg-rose-50/50'
                  )}
                  data-testid={`review-response-${resp.submission_id}-asq`}
                >
                  <p className={cn('text-xs font-semibold uppercase tracking-wide', fb.correct ? 'text-emerald-700' : 'text-rose-700')}>
                    {fb.correct ? 'Correct answer' : 'Not the expected answer'}
                  </p>
                  <p className="mt-1 text-sm leading-6 text-slate-700">
                    {fb.correct ? `We heard “${fb.matched || resp.transcript}”.` : 'Compare what you said with the accepted answers.'}
                  </p>
                  {resp.prompt?.response_spec?.accepted_answers?.length > 0 && (
                    <p className="mt-2 text-xs text-slate-600">
                      Accepted answers: <span className="font-medium text-slate-800">{resp.prompt.response_spec.accepted_answers.join(', ')}</span>
                    </p>
                  )}
                </div>
              )}
              {(fb.band_estimate || fb.clb_band_estimate) && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Band estimate</p>
                  <p className="h-display mt-1 text-2xl">{fb.band_estimate || fb.clb_band_estimate}</p>
                </div>
              )}
              {resp.deterministic_estimate && (
                <div className="border border-slate-200 px-4 py-3" data-testid={`review-response-${resp.submission_id}-content-estimate`}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Content match</p>
                  <p className="mt-1 text-sm text-slate-700">
                    {resp.deterministic_estimate.matched_words} of {resp.deterministic_estimate.target_words} target
                    words in order ({Math.round(resp.deterministic_estimate.content_ratio * 100)}%).
                  </p>
                </div>
              )}
              {fb.feature_tags?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {fb.feature_tags.map((tag) => (
                    <span key={tag} className="rounded bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">{tag}</span>
                  ))}
                </div>
              )}
              {fb.revisions?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Concrete revisions</p>
                  <ul className="mt-2 space-y-2">
                    {fb.revisions.map((rev, i) => (
                      <li key={i} className="border-l-2 border-indigo-600 pl-3 text-sm leading-6 text-slate-700">{rev}</li>
                    ))}
                  </ul>
                </div>
              )}
              {resp.text && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Your response · {resp.word_count} words</p>
                  <p className="mt-2 whitespace-pre-wrap border-l-2 border-slate-200 pl-3 text-sm leading-6 text-slate-700">{resp.text}</p>
                </div>
              )}
              {resp.transcript && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">What we heard</p>
                  <p className="mt-2 border-l-2 border-slate-200 pl-3 text-sm leading-6 text-slate-700">{resp.transcript}</p>
                </div>
              )}
              {open && !zeroed && resp.section === 'speaking' && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Your recording</p>
                  <SignedAudioPlayer
                    exam={exam}
                    submissionId={resp.submission_id}
                    className="mt-2 w-full"
                    testId={`review-response-${resp.submission_id}-audio`}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function MockReviewPage() {
  const { exam, form } = useParams();
  const [params, setParams] = useSearchParams();
  const at = params.get('at') || '';
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!at) { setError('Missing sitting date.'); return; }
    api.mockReview(exam, form, at)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Failed to load the sitting review.'));
  }, [exam, form, at]);

  const order = SECTION_ORDER[exam] || SECTION_ORDER.celpip;
  const sections = useMemo(() => data?.sections || {}, [data]);
  const availableSections = useMemo(
    () => order.filter((s) => sections[s]),
    [order, sections]
  );
  const activeSection = params.get('section') || availableSections[0] || order[0];

  if (error) return (
    <AppShell>
      <Link to={`/${exam}/results`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="review-back">
        <ArrowLeft className="h-4 w-4" /> Results
      </Link>
      <p className="mono-stat mt-6">{exam} · mock review</p>
      <h1 className="h-display mt-2 text-3xl">Couldn’t load the sitting.</h1>
      <p className="mt-2 text-sm text-slate-500">{error}</p>
    </AppShell>
  );
  if (!data) return <AppShell><p className="py-10 text-slate-400">Loading…</p></AppShell>;

  const sec = sections[activeSection] || {};
  const totalCorrect = Object.values(sections).filter((v) => v.kind === 'auto').reduce((a, v) => a + (v.n_correct || 0), 0);
  const totalTotal = Object.values(sections).filter((v) => v.kind === 'auto').reduce((a, v) => a + (v.n_total || 0), 0);

  return (
    <AppShell>
      <Link to={`/${exam}/results`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="review-back">
        <ArrowLeft className="h-4 w-4" /> Results
      </Link>

      <p className="mono-stat mt-6">{exam} · mock review</p>
      <h1 className="h-display mt-2 text-3xl" data-testid="review-title">Mock {mockLabel(Number(form))}</h1>
      <p className="mt-1 text-sm text-slate-500">
        {when(data.at)} · {totalTotal > 0 ? `${totalCorrect} / ${totalTotal} auto-scored correct` : 'Writing & speaking only'}
      </p>

      <div className="mt-8 flex flex-wrap border-b-2 border-slate-900" data-testid="review-tabs">
        {availableSections.map((s) => {
          const M = SECTION_META[s];
          const Icon = M?.icon;
          const bucket = sections[s] || {};
          const badge = bucket.kind === 'auto'
            ? `${bucket.n_correct}/${bucket.n_total}`
            : `${(bucket.responses || []).length} response${(bucket.responses || []).length === 1 ? '' : 's'}`;
          return (
            <button
              key={s}
              type="button"
              data-testid={`review-tab-${s}`}
              onClick={() => setParams({ at, section: s }, { replace: true })}
              className={cn(
                'flex items-center gap-2 px-5 py-3 text-sm font-semibold capitalize transition-colors',
                s === activeSection ? 'bg-slate-900 text-white' : 'text-slate-400 hover:text-slate-900'
              )}
            >
              {Icon && <Icon className="h-4 w-4" />}
              {M?.label || s}
              <span className={cn('ml-1 text-xs font-normal tabular-nums', s === activeSection ? 'text-slate-300' : 'text-slate-400')}>
                {badge}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-6 max-w-3xl border border-slate-200 bg-white" data-testid={`review-section-${activeSection}`}>
        {sec.kind === 'auto' && (
          <>
            <div className="flex items-baseline justify-between border-b border-slate-100 bg-slate-50/70 px-5 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {sec.n_correct} of {sec.n_total} correct
              </p>
              <p className={cn('h-display text-lg tabular-nums', pctCls(sec.n_total ? Math.round((sec.n_correct / sec.n_total) * 100) : 0))}>
                {sec.n_total ? Math.round((sec.n_correct / sec.n_total) * 100) : 0}%
              </p>
            </div>
            {sec.legacy && (
              <div className="border-b border-amber-100 bg-amber-50/50 px-5 py-3 text-xs leading-5 text-amber-900" data-testid={`review-section-${activeSection}-legacy-banner`}>
                This sitting was recorded before per-question tracking was
                enabled, so we don’t have your individual picks. Showing the
                passages and correct answers so you can still study them.
              </div>
            )}
            {(sec.results || []).map((row, i) => (
              <QuestionReviewRow key={`${row.set_id || ''}-${row.qid}-${i}`} row={row} index={i} />
            ))}
            {(!sec.results || sec.results.length === 0) && (
              <p className="px-5 py-6 text-sm text-slate-500">No per-question detail available for this section.</p>
            )}
          </>
        )}
        {sec.kind === 'responses' && (
          <>
            {(sec.responses || []).map((r) => (
              <ResponseRow key={r.submission_id} resp={r} exam={exam} defaultOpen={(sec.responses || []).length === 1} />
            ))}
            {(!sec.responses || sec.responses.length === 0) && (
              <p className="px-5 py-6 text-sm text-slate-500">No responses recorded for this section.</p>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}

